import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT } from "@/lib/data";

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-white/60 text-[15px] leading-relaxed">{children}</div>
  </div>
);

export default function Privacy() {
  const updated = "July 12, 2025";
  return (
    <main className="min-h-screen bg-[#0a1526] text-white font-body">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Privacy Policy</h1>
        <p className="mt-4 text-white/45 text-sm">Last updated: {updated}</p>

        <p className="mt-8 text-white/60 text-[15px] leading-relaxed">
          Link-Lite Data LLC ("Link-Lite Data," "we," "us," or "our") respects your privacy. This Privacy
          Policy explains what information we collect through {CONTACT.website} and our related services, how
          we use it, and the choices you have.
        </p>

        <Section title="Information We Collect">
          <p><strong className="text-white/80">Information you give us.</strong> When you submit a service or
          contact request, we collect the details you provide — such as your name, phone number, email
          address, the service you're interested in, and any message you send us.</p>
          <p><strong className="text-white/80">Information collected automatically.</strong> When you visit our
          website, we collect basic analytics such as pages visited, referring website, approximate location
          (city/region/country derived from your IP address), and general device/browser information. We use
          this to understand traffic and improve the site.</p>
          <p><strong className="text-white/80">Cookies &amp; local storage.</strong> We use your browser's
          local storage for essential functionality (for example, to remember an administrator session or to
          exclude our own team from analytics). We do not sell this data.</p>
          <p><strong className="text-white/80">Push notifications.</strong> If you opt in, we store a browser
          push-notification subscription so we can alert our team about new requests. You can revoke this at
          any time in your browser settings.</p>
        </Section>

        <Section title="How We Use Your Information">
          <p>We use the information we collect to respond to your requests and provide services; to operate,
          maintain and improve our website and apps; to process payments you initiate; to communicate with
          you about your inquiries; and to protect the security and integrity of our services.</p>
        </Section>

        <Section title="Payments">
          <p>Payments and bookings are processed securely by <strong className="text-white/80">Stripe</strong>.
          When you make a purchase, your payment card details are provided directly to Stripe and are handled
          under Stripe's own privacy and security practices. We do not store your full card information.</p>
        </Section>

        <Section title="Third-Party Services">
          <p>We rely on a limited set of trusted third parties to run our services, including Stripe (payments),
          an IP-geolocation provider (approximate visitor location for analytics), and standard web hosting
          infrastructure. These providers process data only as needed to deliver their service.</p>
        </Section>

        <Section title="How We Share Information">
          <p>We do not sell your personal information. We share it only with the service providers described
          above, when required by law, or to protect our rights, safety, and property or that of others.</p>
        </Section>

        <Section title="Data Retention &amp; Security">
          <p>We retain request and analytics data only as long as needed for the purposes described here, and
          we take reasonable measures to protect your information. No method of transmission or storage is
          100% secure, but we work to safeguard your data.</p>
        </Section>

        <Section title="Your Choices">
          <p>You can contact us at any time to ask what information we hold about you, or to request that we
          update or delete it. You can disable cookies/local storage and push notifications through your
          browser settings.</p>
        </Section>

        <Section title="Children's Privacy">
          <p>Our services are intended for adults. Certain apps (such as cannabis-related tools) are restricted
          to users 21 and older. We do not knowingly collect information from children.</p>
        </Section>

        <Section title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated"
          date above.</p>
        </Section>

        <Section title="Contact Us">
          <p>
            If you have questions about this Privacy Policy or your information, contact us:
          </p>
          <p className="text-white/70">
            Link-Lite Data LLC<br />
            Email: <a href={CONTACT.emailHref} className="text-[#60A5FA] hover:text-white transition-colors">{CONTACT.email}</a><br />
            Phone: <a href={CONTACT.phoneHref} className="text-[#60A5FA] hover:text-white transition-colors">{CONTACT.phone}</a><br />
            {CONTACT.area}
          </p>
        </Section>
      </div>
    </main>
  );
}
